import { useEffect, useState, useRef } from 'react';

/**
 * IDs of collapsible toolbar items, ordered by collapse priority
 * (first to collapse → last). Discard and Save are never collapsed.
 */
export type CollapsibleToolbarItem = 'redo' | 'undo' | 'color' | 'tool';

/** All collapsible items in collapse order (lowest priority first). */
const COLLAPSE_ORDER: CollapsibleToolbarItem[] = ['redo', 'undo', 'color', 'tool'];

/**
 * Observes the toolbar and progressively collapses items when the toolbar
 * content overflows its container.
 *
 * Instead of using hardcoded width constants (which drift from actual
 * rendered sizes across breakpoints, font sizes, and variable button text),
 * this hook compares the toolbar's scrollWidth against its clientWidth.
 * When content overflows, it collapses one item per cycle. The
 * ResizeObserver fires again after React re-renders (removing the item),
 * and the check repeats until the content fits. When the toolbar grows
 * (e.g. window resize), it restores items one at a time.
 *
 * @param toolbarRef - Ref to the toolbar container div
 * @returns Set of item IDs that should be moved to the overflow menu
 */
export function useResponsiveToolbar(
  toolbarRef: React.RefObject<HTMLElement | null>,
): Set<CollapsibleToolbarItem> {
  const [overflowed, setOverflowed] = useState<Set<CollapsibleToolbarItem>>(new Set());
  const lastWidthRef = useRef(0);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      // Check the LEFT section for overflow. The toolbar uses
      // justify-content: space-between, so the toolbar itself never
      // reports scrollWidth > clientWidth — flex just overlaps the two
      // sections. But the left section (min-width: 0) will have
      // scrollWidth > clientWidth when its items don't fit, because
      // the right section (flex-shrink: 0) takes priority.
      const leftSection = el.querySelector('.kollab-annotation-toolbar-left') as HTMLElement | null;
      const isOverflowing = leftSection
        ? leftSection.scrollWidth > leftSection.clientWidth + 1
        : el.scrollWidth > el.clientWidth + 1;
      const containerWidth = el.clientWidth;
      const grew = containerWidth > lastWidthRef.current;
      lastWidthRef.current = containerWidth;

      if (isOverflowing) {
        // Content overflows — collapse next lowest-priority item.
        // React re-renders, removing the item. ResizeObserver fires
        // again. If still overflowing, collapse another. Converges.
        setOverflowed((prev) => {
          for (const id of COLLAPSE_ORDER) {
            if (!prev.has(id)) {
              const next = new Set(prev);
              next.add(id);
              return next;
            }
          }
          return prev; // all collapsed already
        });
      } else if (grew && overflowed.size > 0) {
        // Container grew — try restoring the highest-priority collapsed
        // item. If it causes overflow, the next ResizeObserver cycle
        // will collapse it again.
        setOverflowed((prev) => {
          if (prev.size === 0) return prev;
          // Restore the last item that was collapsed (highest priority)
          const reversed = [...COLLAPSE_ORDER].reverse();
          for (const id of reversed) {
            if (prev.has(id)) {
              const next = new Set(prev);
              next.delete(id);
              return next;
            }
          }
          return prev;
        });
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [toolbarRef, overflowed.size]);

  return overflowed;
}
